
import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SearchableSelect from "../../../components/shared/SearchableSelect";
import { questionBankAPI } from "../../../services/api";

const CreateQuestionBankModal = ({ 
  open, 
  onOpenChange, 
  userCategories, 
  fetchFilteredCategories,
  companyCategories, 
  questionTypes, 
  onSuccess,
  showToast 
}) => {
  const validationCreateSchema = Yup.object({
    questionBankName: Yup.string().required("Question bank name is required"),
    questionBankDescription: Yup.string().required(
      "Question bank description is required"
    ),
    category: Yup.string().required("Category is required"),
    subCategory: Yup.string().required("Sub-category is required"),
    questionTypes: Yup.string().required("Question type is required"),
  });

  const createQuestionBanks = async (values) => {
    try {
      const response = await questionBankAPI.create(values);
      if (response.status === 201) {
        showToast("success", "Success", response?.data?.message);
        onOpenChange(false);
        onSuccess();
        createFormik.resetForm();
      }
    } catch (error) {
      console.error("Registration error:", error);
      showToast(
        "error",
        "Error",
        error.response?.data?.message ||
          "Question Bank Creation failed. Please try again."
      );
    }
  };

  const createFormik = useFormik({
    initialValues: {
      questionBankName: "",
      questionBankDescription: "",
      category: "",
      subCategory: "",
      questionTypes: "",
    },
    validationSchema: validationCreateSchema,
    onSubmit: createQuestionBanks,
  });

  const renderSearchableSelectField = (
    name,
    value,
    onValueChange,
    placeholder,
    userItems,
    companyItems,
    error,
    touched
  ) => (
    <div>
      <Label htmlFor={name}>{placeholder}*</Label>
      <SearchableSelect
        value={value}
        onValueChange={onValueChange}
        placeholder={placeholder}
        userItems={userItems}
        companyItems={companyItems}
      />
      {touched && error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  );

  const renderSelectField = (
    name,
    value,
    onValueChange,
    placeholder,
    options,
    error,
    touched
  ) => (
    <div>
      <Label htmlFor={name}>{placeholder}*</Label>
      <Select name={name} value={value} onValueChange={onValueChange}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.length > 0 ? (
            options.map((option, index) => (
              <SelectItem
                key={option._id || index}
                value={option.name || option}
              >
                {option.name || option}
              </SelectItem>
            ))
          ) : (
            <SelectItem value="null">No options available</SelectItem>
          )}
        </SelectContent>
      </Select>
      {touched && error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        onOpenChange(false);
        createFormik.resetForm();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a New Question Bank</DialogTitle>
          <DialogDescription>
            Add a new Question Bank to manage your questions.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={createFormik.handleSubmit}>
          <div className="grid gap-4 py-4 mt-2">
            <div className="grid grid-row-2 gap-4">
              <div>
                <Label htmlFor="questionBankName">Question Bank Name*</Label>
                <Input
                  name="questionBankName"
                  value={createFormik.values.questionBankName}
                  placeholder="Add a question bank name here"
                  onChange={(e) =>
                    createFormik.setFieldValue("questionBankName", e.target.value)
                  }
                  onBlur={() => {createFormik.handleBlur;fetchFilteredCategories(createFormik.values.questionBankName)}}
                />
                {createFormik.touched.questionBankName && createFormik.errors.questionBankName && (
                  <p className="text-sm text-red-500 mt-1">
                    {createFormik.errors.questionBankName}
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="questionBankDescription">Description*</Label>
                <Textarea
                  name="questionBankDescription"
                  value={createFormik.values.questionBankDescription}
                  placeholder="Add a question bank description here"
                  onChange={(e) =>
                    createFormik.setFieldValue("questionBankDescription", e.target.value)
                  }
                  onBlur={createFormik.handleBlur}
                />
                {createFormik.touched.questionBankDescription && createFormik.errors.questionBankDescription && (
                  <p className="text-sm text-red-500 mt-1">
                    {createFormik.errors.questionBankDescription}
                  </p>
                )}
              </div>

              {renderSearchableSelectField(
                "category",
                createFormik.values.category,
                (value) => createFormik.setFieldValue("category", value),
                "Category",
                userCategories,
                companyCategories,
                createFormik.errors.category,
                createFormik.touched.category
              )}

              {renderSearchableSelectField(
                "subCategory",
                createFormik.values.subCategory,
                (value) => createFormik.setFieldValue("subCategory", value),
                "Sub-Category",
                (() => {
                  const selectedCategory = userCategories.find(
                    (cat) => cat.name === createFormik.values.category
                  );
                  return selectedCategory ? selectedCategory.subCategories : [];
                })(),
                (() => {
                  const selectedCategory = companyCategories.find(
                    (cat) => cat.name === createFormik.values.category
                  );
                  return selectedCategory ? selectedCategory.subCategories : [];
                })(),
                createFormik.errors.subCategory,
                createFormik.touched.subCategory
              )}

              {renderSelectField(
                "questionTypes",
                createFormik.values.questionTypes,
                (value) => createFormik.setFieldValue("questionTypes", value),
                "Question Type",
                questionTypes,
                createFormik.errors.questionTypes,
                createFormik.touched.questionTypes
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit">Create Question Bank</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateQuestionBankModal;